import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppUseDispatch, AppUseSelector } from '../../../redux/hooks'
import { SET_ONBOARDING_NAME } from '../../../redux/types'
import { saveFirstName } from '../../../redux/actions/myAccountAction'
import { trackOnboarding } from '../../../utils/analytics'
import { StepTitle } from '../stepContext'

/**
 * A first name guessed from an email address, so this question opens with an
 * answer already in it and can be accepted with a click.
 *
 * Takes the local part up to the first separator — "joanne.kor@…" gives
 * "Joanne", "sam_lee99@…" gives "Sam" — and gives up rather than guessing
 * badly when there is nothing name-shaped left.
 */
export const nameFromEmail = (email: string): string => {
  const local = (email || '').split('@')[0] || ''
  const first = local.split(/[._+\-0-9]/)[0] || ''
  if (first.length < 2) return ''
  return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase()
}

const NameStep = ({ next }: { next: () => void }) => {
  const { t } = useTranslation('onboarding')
  const dispatch = AppUseDispatch()
  const { name, email } = AppUseSelector(({ onboarding }) => onboarding)
  // Seeded from the address they just verified. Left as a suggestion rather
  // than committed: the button stays up so accepting it is still a deliberate
  // click, and editing it works exactly as typing it from scratch did.
  const [value, setValue] = useState(name || nameFromEmail(email))
  const [error, setError] = useState<string | null>(null)
  // Once this is answered the question below it carries the flow, so this
  // button retires rather than sit there competing with the next one. It comes
  // back if the name is edited, so the edit can be committed.
  const [submitted, setSubmitted] = useState(Boolean(name))

  const submit = (e?: React.FormEvent) => {
    e?.preventDefault()
    const trimmed = value.trim()
    if (!trimmed) {
      setError(t('name.required'))
      return
    }
    setError(null)
    setSubmitted(true)
    dispatch({ type: SET_ONBOARDING_NAME, payload: trimmed })
    trackOnboarding('name_entered', { seeded_from_email: trimmed === nameFromEmail(email) })
    // This question now sits after the gate, so registration has already
    // happened under a name the backend invented from the address. Persist
    // what the user actually confirmed. Fire-and-forget: a failed write must
    // not strand them on a question they have answered.
    if (localStorage.getItem('authToken')) {
      saveFirstName({ firstName: trimmed }).catch(() => undefined)
    }
    next()
  }

  return (
    <div className='ob-step'>
      <StepTitle>{t('name.title')}</StepTitle>
      <form onSubmit={submit} noValidate>
        <input
          type='text'
          autoComplete='given-name'
          className='ob-input'
          placeholder={t('name.placeholder')}
          value={value}
          maxLength={60}
          onChange={(e) => {
            setValue(e.target.value)
            setError(null)
            setSubmitted(false)
          }}
        />
        {error && <p className='ob-error'>{error}</p>}
        {!submitted && (
          <div className='ob-actions'>
            <button type='submit' className='ob-cta ob-cta--block'>
              {t('nav.continue')}
            </button>
          </div>
        )}
      </form>
    </div>
  )
}

export default NameStep
